// =====================================================================
// src/lib/balcao.js — a busca do balcão, sem DOM
//
// Pedido do Hugo em 19/09/2026: a recepcionista digita o nome com o
// hóspede parado na frente dela, e a reserva abre num quadro, com o que
// veio do pré-check-in de um lado e o que falta preencher do outro.
//
// Três coisas moram aqui, e nenhuma delas precisa de navegador:
//
//   1. quando a busca vale a pena ser feita   (`buscaPronta`)
//   2. em que pé está cada reserva achada     (`situacaoDaLinha`)
//   3. o que ainda falta para entregar a chave (`faltaParaAChave`)
//
// O corte de QUEM vê o quê não está aqui — está na 0040, no `where` da
// `buscar_no_balcao`. Tela que decide permissão sozinha é tela que um dia
// decide errado.
// =====================================================================
import { camposObrigatorios, faltaNaFicha } from './hospedes.js';

// ── QUANDO PROCURAR ──────────────────────────────────────────────────
// Três caracteres, o mesmo piso da 0040. Está dos dois lados de
// propósito: o do banco é a regra, e este existe só para não disparar uma
// ida ao servidor a cada tecla das duas primeiras letras. Se um dia os
// dois divergirem, quem manda é o banco — a tela apenas para de perguntar
// cedo demais.
export const MINIMO_BUSCA = 3;
export const buscaPronta = t => String(t == null ? '' : t).trim().length >= MINIMO_BUSCA;

// ── EM QUE PÉ ESTÁ A RESERVA ─────────────────────────────────────────
// A lista de resultados precisa dizer, numa olhada, por que aquela linha
// está ali. Cinco estados, e eles não são os mesmos das abas do dia: aqui
// aparece reserva de semana que vem, que a lista do dia nunca mostra.
//
// CORREÇÃO, 19/09/2026 — a prova contra o banco de verdade
// (`prova_balcao.mjs`) derrubou o que estava escrito aqui. Eu dizia que
// `atrasada` era o que justificava a busca existir, porque o hóspede que
// devia ter chegado ontem "some da lista de hoje". Não some: a janela da
// `checkin_do_dia` é `check_in <= dia and check_out >= dia` (0022, linha
// 54 — a 0020 também a define, mas a 0022 é a ÚLTIMA, e foi nela que eu
// citei errado na primeira vez), então a reserva dele ATRAVESSA hoje e
// está na lista, em outra aba. O estado continua valendo como informação — "esta pessoa devia ter
// chegado ontem" é o que a recepcionista precisa saber —, mas não é o
// motivo da barra.
//
// O motivo da barra é `futura`: a reserva que NÃO cruza hoje é a única
// que a lista do dia não tem como mostrar. Isso, e o quadro com a ficha,
// que a lista do dia não abre.
export function situacaoDaLinha(l, hoje) {
  const h = String(hoje || '');
  const ci = String((l || {}).check_in || '');
  const co = String((l || {}).check_out || '');
  const dentro = Number((l || {}).dentro || 0) > 0;
  if (dentro) return co === h
    ? { chave: 'sai', rotulo: 'Sai hoje', classe: 'badge-orange' }
    : { chave: 'dentro', rotulo: 'Hospedado', classe: 'badge-green' };
  if (ci === h) return { chave: 'chega', rotulo: 'Chega hoje', classe: 'badge-blue' };
  if (ci < h) return { chave: 'atrasada', rotulo: 'Chegada atrasada', classe: 'badge-red' };
  return { chave: 'futura', rotulo: 'Chega depois', classe: 'badge-gray' };
}

// ── O QUE FALTA PARA A CHAVE ─────────────────────────────────────────
// A 0017 trava o check-in sem documento, e a 0038 devolve `completa` por
// pessoa. Isto diz à recepcionista, em palavras, o que ela tem de
// perguntar ao hóspede — "incompleto" sozinho faz procurar.
//
// A ficha da FNRH NÃO entra nesta conta, e a diferença importa: sem
// documento a chave não sai (o banco recusa); sem profissão a chave sai e
// a FNRH fica devendo. Misturar as duas faria a recepção tratar como
// impedimento o que é pendência.
export function faltaParaAChave(p) {
  const f = [];
  if (!String((p || {}).documento || '').trim()) f.push('documento');
  if (!(p || {}).data_nascimento) f.push('data de nascimento');
  return f;
}

export const podeEntregarChave = pessoas =>
  Array.isArray(pessoas) && pessoas.length > 0 && pessoas.every(p => faltaParaAChave(p).length === 0);

// ── OS DOIS BLOCOS, QUE FOI ESCOLHA DO HUGO ──────────────────────────
// Ele pediu "as duas coisas, em blocos separados": o que o hóspede
// preencheu no link e o que a recepção preenche no balcão. A `origem` que
// a 0038 devolve é DERIVADA (existe linha em `precheckin_pessoas`
// apontando para o hóspede), não guardada — e é ela que decide o selo.
export function origemDaFicha(p) {
  return (p || {}).origem === 'hospede'
    ? { chave: 'hospede', rotulo: 'Preenchido pelo hóspede', classe: 'badge-green' }
    : { chave: 'recepcao', rotulo: 'Preenchido no balcão', classe: 'badge-gray' };
}

// Quantos dos onze campos da FNRH já têm conteúdo. Serve para o bloco
// dizer "7 de 11" em vez de abrir onze campos vazios sem aviso.
// 0062: conta os OBRIGATÓRIOS, pela mesma régua da tela de Hóspedes
// (`faltaNaFicha`) — duas contas diferentes para "ficha completa" dariam
// "12 de 12" num lugar e "incompleta" no outro.
// 0065: a régua depende do hotel (`coleta`: módulo FNRH contratado) e da
// pessoa (gênero "Outro" pede a descrição) — por isso o total também é
// função, e não constante.
export function fnrhPreenchidos(p, coleta = false) {
  return totalFnrh(p, coleta) - faltaNaFicha(p, coleta).length;
}

export const totalFnrh = (p, coleta = false) => camposObrigatorios(p, coleta).length;

// ── A LINHA ESCOLHIDA CONTINUA ESCOLHIDA ─────────────────────────────
// Depois de gravar, a tela repete a busca — e a reserva aberta tem de
// continuar aberta, senão a recepcionista perde o lugar no meio do
// atendimento. Isto acha a mesma reserva na lista nova, e devolve null
// quando ela saiu do alcance (o hóspede fez check-out, por exemplo), que
// é quando fechar o quadro é o certo.
export function reencontrar(linhas, reservaId) {
  if (!reservaId) return null;
  return (linhas || []).find(l => l && l.reserva_id === reservaId) || null;
}

// ── O CABEÇALHO DO QUADRO ────────────────────────────────────────────
// `ficha_da_reserva` devolve uma linha POR PESSOA, e os dados da reserva
// vêm repetidos em todas. Separar aqui evita que a tela leia `[0]` e
// dependa da ordem que o banco devolveu.
export function separarFicha(linhas) {
  const l = (linhas || []).filter(Boolean);
  if (!l.length) return { reserva: null, pessoas: [] };
  const p = l[0];
  return {
    reserva: {
      id: p.reserva_id, quarto: p.quarto, andar: p.andar, capacidade: p.capacidade,
      // `situacao_reserva`, e não `situacao`: a 0038 desambigua o nome
      // porque a linha carrega reserva e pessoa juntas. Ler `p.situacao`
      // aqui daria `undefined` calado.
      check_in: p.check_in, check_out: p.check_out, situacao: p.situacao_reserva,
      plataforma: p.plataforma, numero: p.numero, imobiliaria: p.imobiliaria,
    },
    // titular primeiro, depois por nome — a mesma ordem da lista do dia
    pessoas: l.slice().sort((a, b) => (b.principal - a.principal) || String(a.nome).localeCompare(String(b.nome))),
  };
}

// ── O INÍCIO DO ATENDIMENTO (0060) ───────────────────────────────────
// Hugo, 24/09/2026: o tempo de check-in no balcão começa quando a recepção
// ABRE a reserva nesta tela, e termina na chegada do primeiro hóspede.
// Vale para a chegada: reserva confirmada, hoje dentro da estadia (dia do
// hotel; a atrasada entra — 0061) e ninguém chegou ainda. Abrir a ficha de quem já está hospedado,
// ou de uma chegada futura, não é atendimento de chegada — e o banco
// recusaria a concluída. A primeira abertura é a que vale; o banco ignora
// as seguintes.
export function iniciaAtendimento(ficha, hoje) {
  if (!ficha || !ficha.reserva) return false;
  const r = ficha.reserva;
  // 0061: dentro da estadia — a chegada atrasada também é atendida
  if (r.situacao !== 'confirmada' || r.check_in > hoje || r.check_out < hoje) return false;
  return !(ficha.pessoas || []).some(p => p && p.chegada_em);
}
